import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './wizardEnquiryForm.module.css';
import { Back, Close } from '../../../Svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { sendWizardEnquiryThunk } from '../../redux/wizardSlice';

const WizardEnquiryForm = () => {
    const location = useLocation();


    const data = location.state


    const { loading } = useSelector(state => state.wizard)
    const navigate = useNavigate()
    const dispatch = useDispatch()

    // Required date cannot be in the past
    const today = new Date().toISOString().split('T')[0];


    const getBreakQtyDetails = (quantityTable, quantity) => {
        if (!Array.isArray(quantityTable) || quantityTable.length === 0) {
            return { breakQty: 1, unitPrice: 0 };
        }
        const sortedTable = [...quantityTable]?.sort((a, b) => (a?.breakQty || 0) - (b?.breakQty || 0));
        return sortedTable.find((entry) => quantity <= entry?.breakQty) || sortedTable[sortedTable.length - 1] || { breakQty: 1, unitPrice: 0 };
    };

    // Price table of a part, looked up by part number so it stays right after a part is removed
    const getQuantityTable = (productNumber) =>
        data?.find((item) => item?.productNumber === productNumber)?.product?.quantityTable;


    const formik = useFormik({
        initialValues: {
            partList: (data || []).map(item => {
                const { unitPrice } = getBreakQtyDetails(item?.product?.quantityTable, item?.quantity)

                return ({
                    productNumber: item.productNumber || '',
                    manufacturer: item.manufacturer || '',
                    quantity: item.quantity || '',
                    expectedPrice: item.expectedPrice || '',
                    requiredDate: item.requiredDate || '',
                    masterPrice: (unitPrice * item?.quantity) || 0
                })
            }
            )
        },
        validationSchema: Yup.object({
            partList: Yup.array().of(
                Yup.object().shape({
                    quantity: Yup.number().typeError('Enter a valid number').positive('Must be more than 0').required('Required'),
                    expectedPrice: Yup.number().typeError('Enter a valid price').min(0, 'Cannot be negative').required('Required'),
                    requiredDate: Yup.string()
                })
            )
        }),
        onSubmit: async values => {
            const status = await dispatch(sendWizardEnquiryThunk(values))
            if (status.payload == 200) {
                navigate(-1)
            }
        }
    });

    const removePart = (index) => {
        formik.setFieldValue('partList', formik.values.partList.filter((_, i) => i !== index));
    };

    const partCount = formik.values.partList.length;

    return (
        <>
            <div className={styles.imgBread}>
                <div className={styles.breadContent}>
                    <h1 className={styles.breadTitle}>Wizard Tool</h1>
                    <p className={styles.breadSubtitle}>Request a quote for the parts you selected.</p>
                </div>
            </div>
            <div className={styles.exInContainer}>
                <div>
                    <button className={styles.compBtn} onClick={() => navigate(-1)}><Back />Back to previous page</button>
                </div>

                {partCount === 0 ?
                    <div className={styles.exInForm}>
                        <div className={styles.emptyState}>
                            <span className={styles.emptyTitle}>No parts selected</span>
                            <span className={styles.emptyText}>Select parts in the Wizard Tool first, then request a quote here.</span>
                            <button type="button" className={styles.submitBtn} onClick={() => navigate('/wizardUpload')}>Go to Wizard Tool</button>
                        </div>
                    </div>
                    :
                    <div className={styles.exInForm}>
                        <div className={styles.formIntro}>
                            <span className={styles.previewTitle}>Quote request</span>
                            <span className={styles.introText}>Please fill out the quote request form below and an Electrowiz representative will contact you shortly.</span>
                        </div>

                        <form onSubmit={formik.handleSubmit}>
                            <div className={styles.partsHeader}>
                                <span className={styles.previewTitle}>Part list</span>
                                <span className={styles.partsCount}>{partCount} part{partCount !== 1 ? 's' : ''}</span>
                            </div>

                            <div className={styles.partCards}>
                                {formik.values.partList.map((item, index) => (
                                    <div className={styles.partCard} key={`${item.productNumber}-${index}`}>
                                        <div className={styles.partCardHead}>
                                            <span className={styles.partIndex}>{index + 1}</span>
                                            <div className={styles.partInfo}>
                                                <span className={styles.partNumber}>{item.productNumber}</span>
                                                <span className={styles.partMfg}>{item.manufacturer || 'Manufacturer not specified'}</span>
                                            </div>
                                            {partCount > 1 && (
                                                <button
                                                    type="button"
                                                    className={styles.removeBtn}
                                                    onClick={() => removePart(index)}
                                                    aria-label={`Remove ${item.productNumber}`}
                                                    title="Remove part"
                                                >
                                                    <Close />
                                                </button>
                                            )}
                                        </div>

                                        <div className={styles.qrForminputs}>
                                            <div className={styles.loginInputGroup}>
                                                <label>Required Quantity*</label>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    name={`partList[${index}].quantity`}
                                                    value={formik.values.partList[index].quantity}
                                                    onChange={(e) => {
                                                        formik.handleChange(e);
                                                        const newQuantity = Number(e.target.value) || 0;
                                                        const unitPrice = getBreakQtyDetails(getQuantityTable(item.productNumber), newQuantity)?.unitPrice || 0;
                                                        formik.setFieldValue(`partList[${index}].masterPrice`, unitPrice * newQuantity);
                                                    }}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="Enter quantity"
                                                />

                                                {formik.touched.partList?.[index]?.quantity && formik.errors.partList?.[index]?.quantity && (
                                                    <div className={styles.error}>{formik.errors.partList[index].quantity}</div>
                                                )}
                                            </div>
                                            <div className={styles.loginInputGroup}>
                                                <label>Expected Price*</label>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    name={`partList[${index}].expectedPrice`}
                                                    value={formik.values.partList[index].expectedPrice}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="Enter expected price"
                                                />
                                                {formik.touched.partList?.[index]?.expectedPrice && formik.errors.partList?.[index]?.expectedPrice && (
                                                    <div className={styles.error}>{formik.errors.partList[index].expectedPrice}</div>
                                                )}
                                            </div>
                                            <div className={styles.loginInputGroup}>
                                                <label>Required Date <span className={styles.optional}>(optional)</span></label>
                                                <input
                                                    type="date"
                                                    min={today}
                                                    name={`partList[${index}].requiredDate`}
                                                    value={formik.values.partList[index].requiredDate}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                                {formik.touched.partList?.[index]?.requiredDate && formik.errors.partList?.[index]?.requiredDate && (
                                                    <div className={styles.error}>{formik.errors.partList[index].requiredDate}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className={styles.formActions}>
                                <button type="button" className={styles.cancelBtn} onClick={() => navigate(-1)}>Back</button>
                                <button type="submit" className={styles.submitBtn} disabled={loading}>{loading ? "Please wait..." : "Submit"}</button>
                            </div>
                        </form>
                    </div>
                }
            </div>
        </>
    );
};

export default WizardEnquiryForm;
